import generatedGameAssets from "./generated/game-assets.json";

export type LocaleCode = "en" | "ru";

export type LocalizedText = Record<LocaleCode, string>;

export type StoreLinkKey = "appStore" | "googlePlay";

export type GameStoreLinks = Partial<Record<StoreLinkKey, string>>;

export interface GameGalleryImage {
  readonly src: string;
  readonly alt: LocalizedText;
  readonly tone: string;
}

export interface GameHowToPlayStep {
  readonly title: LocalizedText;
  readonly body: LocalizedText;
  readonly primitive: "node" | "edge" | "glow";
}

export interface GameModeShowcase {
  readonly id: string;
  readonly title: LocalizedText;
  readonly body: LocalizedText;
  readonly tag: LocalizedText;
}

export interface GameSiteSection {
  readonly id: string;
  readonly eyebrow: LocalizedText;
  readonly title: LocalizedText;
  readonly body: LocalizedText;
  readonly image?: string;
  readonly images?: readonly string[];
}

export interface GameSiteContent {
  readonly storeMaterialsSource: string;
  readonly howEyebrow?: LocalizedText;
  readonly howTitle?: LocalizedText;
  readonly howToPlay: readonly GameHowToPlayStep[];
  readonly modesEyebrow?: LocalizedText;
  readonly modesTitle?: LocalizedText;
  readonly modes: readonly GameModeShowcase[];
  readonly sections: readonly GameSiteSection[];
}

export interface GameMetadata {
  readonly id: string;
  readonly slug: string;
  readonly title: LocalizedText;
  readonly tagline: LocalizedText;
  readonly description: LocalizedText;
  readonly status: "live" | "coming-soon" | "prototype";
  readonly genre: LocalizedText;
  readonly logo: string;
  readonly heroImage: string;
  readonly heroScreenshots?: readonly string[];
  readonly featuredBackground?: string;
  readonly featuredLogo?: string;
  readonly gradient: string;
  readonly accentColor: string;
  readonly links: GameStoreLinks;
  readonly gallery: readonly GameGalleryImage[];
  readonly highlights: readonly LocalizedText[];
  readonly site: GameSiteContent;
}

export type ShowcaseVisual =
  | "dice-triangles"
  | "witch-broom"
  | "bridges";

export interface GameShowcaseMetadata {
  readonly id: string;
  readonly slug: string;
  readonly title: LocalizedText;
  readonly tagline: LocalizedText;
  readonly description: LocalizedText;
  readonly status: "live" | "coming-soon";
  readonly genre: LocalizedText;
  readonly image?: string;
  readonly imageFit?: "cover" | "contain";
  readonly gradient: string;
  readonly accentColor: string;
  readonly visual: ShowcaseVisual;
}

const diceTrianglesGame = generatedGameAssets.games.dice_triangles;
type GeneratedGameMetadata = Omit<typeof diceTrianglesGame, "links"> & {
  readonly links?: GameStoreLinks;
};
const generatedGames = generatedGameAssets.games as unknown as Record<
  string,
  GeneratedGameMetadata | undefined
>;
const witchBroomGame = generatedGames.witch_broom;
const bridgesGame = generatedGameAssets.games.bridges;
const bridgesAssets = bridgesGame.assets;
const bridgesGallery = Object.fromEntries(
  bridgesAssets.gallery.map((asset) => [asset.id, asset.src])
);
const diceTrianglesAssets = diceTrianglesGame.assets;
const diceTrianglesGallery = Object.fromEntries(
  diceTrianglesAssets.gallery.map((asset) => [asset.id, asset.src])
);
const diceTrianglesSections = Object.fromEntries(
  diceTrianglesAssets.sections.map((section) => [
    section.id,
    section.images ?? (section.image ? [section.image] : [])
  ])
);
const witchBroomAssets = witchBroomGame?.assets ?? {
  icon: "/generated/games/witch-broom/icon-512.webp",
  hero: "/generated/games/witch-broom/hero-1200.webp",
  og: "/generated/games/witch-broom/og-1200x630.jpg",
  card: "/generated/games/witch-broom/card-760.webp",
  featuredBackground: "/generated/games/witch-broom/featured-background-1448.webp",
  featuredLogo: "/generated/games/witch-broom/featured-logo-760.webp",
  gallery: [
    {
      id: "home",
      src: "/generated/games/witch-broom/screenshot-home-540.webp"
    },
    {
      id: "boss",
      src: "/generated/games/witch-broom/screenshot-boss-540.webp"
    },
    {
      id: "duel",
      src: "/generated/games/witch-broom/screenshot-duel-540.webp"
    }
  ],
  sections: [
    {
      id: "boss-campaign",
      image: "/generated/games/witch-broom/section-boss-home-420.webp",
      images: [
        "/generated/games/witch-broom/section-boss-home-420.webp",
        "/generated/games/witch-broom/section-boss-rival-420.webp"
      ]
    },
    {
      id: "tactical-magic",
      image: "/generated/games/witch-broom/section-magic-duel-420.webp",
      images: [
        "/generated/games/witch-broom/section-magic-duel-420.webp",
        "/generated/games/witch-broom/section-magic-broom-420.webp"
      ]
    },
    {
      id: "online-friend-duels",
      image: "/generated/games/witch-broom/section-online-room-420.webp",
      images: ["/generated/games/witch-broom/section-online-room-420.webp"]
    }
  ]
};
const witchBroomGallery = Object.fromEntries(
  witchBroomAssets.gallery.map((asset) => [asset.id, asset.src])
);
const witchBroomSections = Object.fromEntries(
  witchBroomAssets.sections.map((section) => [
    section.id,
    section.images ?? (section.image ? [section.image] : [])
  ])
);

export const siteGames = [
  {
    id: "dice-triangles",
    slug: "dice-triangles",
    title: {
      en: "Dice Triangles",
      ru: "Dice Triangles"
    },
    tagline: {
      en: "Roll the dice. Connect the dots. Capture the board.",
      ru: "Бросайте кубики. Соединяйте точки. Захватывайте поле."
    },
    description: {
      en: "A modern strategy game where dice rolls shape your moves and every line can turn into territory.",
      ru: "Современная стратегическая игра, где броски кубиков задают ходы, а каждая линия может стать территорией."
    },
    status: "live",
    genre: {
      en: "Strategy game",
      ru: "Стратегическая игра"
    },
    logo: diceTrianglesAssets.icon,
    heroImage: diceTrianglesAssets.hero,
    featuredBackground: diceTrianglesAssets.featuredBackground,
    featuredLogo: diceTrianglesAssets.featuredLogo,
    gradient: "linear-gradient(135deg, #7c3cff 0%, #ff3bd5 48%, #ffd84d 100%)",
    accentColor: "#ff4fd8",
    links: diceTrianglesGame.links,
    gallery: [
      {
        src: diceTrianglesGallery.home,
        alt: {
          en: "Dice Triangles home screen",
          ru: "Главный экран Dice Triangles"
        },
        tone: "#6b5cff"
      },
      {
        src: diceTrianglesGallery.game,
        alt: {
          en: "Dice Triangles match board",
          ru: "Игровое поле Dice Triangles"
        },
        tone: "#00d7ff"
      },
      {
        src: diceTrianglesGallery.online,
        alt: {
          en: "Dice Triangles online play",
          ru: "Онлайн-режим Dice Triangles"
        },
        tone: "#ffb800"
      }
    ],
    highlights: [
      {
        en: "Fast turns with tactical depth",
        ru: "Быстрые ходы и тактическая глубина"
      },
      {
        en: "Online rooms, rematches, and rating",
        ru: "Онлайн-комнаты, реванши и рейтинг"
      },
      {
        en: "Solo, local, and real-player modes",
        ru: "Игра против ИИ, локально и с реальными игроками"
      }
    ],
    site: {
      storeMaterialsSource: "tools/config/games/dice_triangles.json:web",
      howToPlay: [
        {
          title: {
            en: "Roll the dice",
            ru: "Бросьте кубики"
          },
          body: {
            en: "Dice shape your available moves.",
            ru: "Кубики задают доступные ходы."
          },
          primitive: "glow"
        },
        {
          title: {
            en: "Connect nodes",
            ru: "Соедините точки"
          },
          body: {
            en: "Draw clean edges between dots.",
            ru: "Проводите рёбра между точками."
          },
          primitive: "edge"
        },
        {
          title: {
            en: "Close triangles",
            ru: "Замкните треугольники"
          },
          body: {
            en: "Capture territory and grow stronger.",
            ru: "Захватывайте территорию и усиливайте позицию."
          },
          primitive: "glow"
        },
        {
          title: {
            en: "Score more",
            ru: "Наберите больше"
          },
          body: {
            en: "Outscore your opponent to win.",
            ru: "Обгоните соперника по очкам."
          },
          primitive: "node"
        }
      ],
      modes: [
        {
          id: "online",
          title: {
            en: "Online matches",
            ru: "Онлайн-матчи"
          },
          body: {
            en: "Play against real players worldwide.",
            ru: "Играйте с реальными игроками по всему миру."
          },
          tag: {
            en: "Online",
            ru: "Онлайн"
          }
        },
        {
          id: "ai",
          title: {
            en: "Play vs AI",
            ru: "Игра против ИИ"
          },
          body: {
            en: "Practice patterns and improve your skills.",
            ru: "Тренируйте паттерны и улучшайте игру."
          },
          tag: {
            en: "AI",
            ru: "ИИ"
          }
        },
        {
          id: "local",
          title: {
            en: "Local play",
            ru: "Локальная игра"
          },
          body: {
            en: "Pass one device around and play with a friend.",
            ru: "Передавайте одно устройство и играйте с другом."
          },
          tag: {
            en: "Local",
            ru: "Локально"
          }
        }
      ],
      sections: [
        {
          id: "online-play",
          eyebrow: {
            en: "Online play",
            ru: "Онлайн"
          },
          title: {
            en: "Create rooms, rematch, climb rating",
            ru: "Создавайте комнаты, играйте реванши, растите в рейтинге"
          },
          body: {
            en: "Lightweight online sessions make each match fast to start and easy to repeat.",
            ru: "Лёгкие онлайн-сессии помогают быстро начать матч и сразу сыграть ещё."
          },
          images: diceTrianglesSections["online-play"]
        },
        {
          id: "strategy-matters",
          eyebrow: {
            en: "Strategy matters",
            ru: "Стратегия важна"
          },
          title: {
            en: "One edge can change the whole board",
            ru: "Одно ребро может изменить всё поле"
          },
          body: {
            en: "Plan ahead, block your opponent, and create chances to capture multiple triangles in a single turn.",
            ru: "Планируйте заранее, блокируйте соперника и создавайте ходы с несколькими треугольниками."
          },
          images: diceTrianglesSections["strategy-matters"]
        }
      ]
    }
  },
  {
    id: "witch-broom",
    slug: "witch-broom",
    title: {
      en: "Witch's Broom",
      ru: "Witch's Broom"
    },
    tagline: {
      en: "A gothic card duel built for long-session tactical play.",
      ru: "Готическая карточная дуэль для долгой тактической игры."
    },
    description: {
      en: "Witch's Broom is a skill-based tactical card duel with magic as a controlled modifier, animated rivals, campaign progression, online friend duels, and cheating boss challenges.",
      ru: "Witch's Broom - тактическая карточная дуэль на мастерство: магия как контролируемый модификатор, анимированные соперники, кампания, онлайн с другом и испытания с нечестными боссами."
    },
    status: "live",
    genre: {
      en: "Gothic card battler",
      ru: "Готический карточный баттлер"
    },
    logo: witchBroomAssets.icon,
    heroImage: witchBroomAssets.hero,
    featuredBackground: witchBroomAssets.featuredBackground,
    featuredLogo: witchBroomAssets.featuredLogo,
    gradient: "linear-gradient(135deg, #2c074d 0%, #7b2bd6 46%, #f2c463 100%)",
    accentColor: "#f2c463",
    links: witchBroomGame?.links ?? {},
    gallery: [
      {
        src: witchBroomGallery.home,
        alt: {
          en: "Witch's Broom home screen",
          ru: "Главный экран Witch's Broom"
        },
        tone: "#7b2bd6"
      },
      {
        src: witchBroomGallery.boss,
        alt: {
          en: "Witch's Broom boss duel",
          ru: "Дуэль с боссом в Witch's Broom"
        },
        tone: "#f2c463"
      },
      {
        src: witchBroomGallery.duel,
        alt: {
          en: "Witch's Broom card duel",
          ru: "Карточная дуэль Witch's Broom"
        },
        tone: "#ff4fd8"
      }
    ],
    highlights: [
      {
        en: "Skill-based tactical card duels",
        ru: "Тактические карточные дуэли на мастерство"
      },
      {
        en: "Animated rivals with escalating boss challenges",
        ru: "Анимированные соперники и усложняющиеся боссы"
      },
      {
        en: "Private online duels with a friend",
        ru: "Приватные онлайн-дуэли с другом"
      },
      {
        en: "Leagues and 2v2 team duels planned for audience scale",
        ru: "Лиги и командные дуэли 2v2 запланированы для роста аудитории"
      }
    ],
    site: {
      storeMaterialsSource:
        "Witch's Broom - Pitch Deck slides 01, 02, 04 + store listing",
      howEyebrow: {
        en: "Product pillars",
        ru: "Опоры продукта"
      },
      howTitle: {
        en: "Skill, campaign, magic, cheating bosses",
        ru: "Мастерство, кампания, магия, нечестные боссы"
      },
      howToPlay: [
        {
          title: {
            en: "Skill-based tactical core",
            ru: "Тактическое ядро на мастерство"
          },
          body: {
            en: "No idle loop: win by reading the board, timing captures, and avoiding traps.",
            ru: "Без idle-цикла: побеждайте чтением стола, таймингом взяток и обходом ловушек."
          },
          primitive: "glow"
        },
        {
          title: {
            en: "Character-driven campaign",
            ru: "Кампания с характерными соперниками"
          },
          body: {
            en: "Opponents are animated rivals with personality, target scores, and escalating difficulty.",
            ru: "Соперники - анимированные rivals с характером, целями по очкам и растущей сложностью."
          },
          primitive: "edge"
        },
        {
          title: {
            en: "Magic as spice",
            ru: "Магия как специя"
          },
          body: {
            en: "Magic adds tactical spikes and PvE drama while competitive modes stay balanced.",
            ru: "Магия добавляет тактические всплески и PvE-драму, а соревновательные режимы остаются сбалансированными."
          },
          primitive: "glow"
        },
        {
          title: {
            en: "Cheating bosses",
            ru: "Нечестные боссы"
          },
          body: {
            en: "A post-victory mode turns defeated bosses into cheaters with a hidden deduction layer.",
            ru: "После победы боссы возвращаются как читеры, добавляя скрытый слой дедукции."
          },
          primitive: "node"
        }
      ],
      modesEyebrow: {
        en: "Game overview",
        ru: "Обзор игры"
      },
      modesTitle: {
        en: "Built for sessions, rivals, and replay",
        ru: "Для длинных сессий, соперников и replay value"
      },
      modes: [
        {
          id: "campaign",
          title: {
            en: "Campaign progression",
            ru: "Прогрессия кампании"
          },
          body: {
            en: "Play against increasingly difficult bosses with target scores and animated identities.",
            ru: "Играйте против всё более сложных боссов с целями по очкам и анимированной идентичностью."
          },
          tag: {
            en: "Campaign",
            ru: "Кампания"
          }
        },
        {
          id: "cheating-bosses",
          title: {
            en: "Cheating boss mode",
            ru: "Режим нечестных боссов"
          },
          body: {
            en: "A distinctive marketable hook: defeated bosses return with unique cheating patterns.",
            ru: "Отличимый маркетинговый hook: побежденные боссы возвращаются с уникальными паттернами жульничества."
          },
          tag: {
            en: "Bosses",
            ru: "Боссы"
          }
        },
        {
          id: "friend-duels",
          title: {
            en: "Play with Friend online",
            ru: "Онлайн-игра с другом"
          },
          body: {
            en: "Private online friend duels are already implemented.",
            ru: "Приватные онлайн-дуэли с другом уже реализованы."
          },
          tag: {
            en: "Online",
            ru: "Онлайн"
          }
        },
        {
          id: "audience-scale",
          title: {
            en: "Audience scale",
            ru: "Рост аудитории"
          },
          body: {
            en: "Leagues and 2v2 team duels are planned for a broader long-session audience.",
            ru: "Лиги и командные дуэли 2v2 запланированы для более широкой аудитории долгих сессий."
          },
          tag: {
            en: "Planned",
            ru: "Планируется"
          }
        }
      ],
      sections: [
        {
          id: "boss-campaign",
          eyebrow: {
            en: "Game overview",
            ru: "Обзор игры"
          },
          title: {
            en: "A gothic card duel built for long-session tactical play",
            ru: "Готическая карточная дуэль для долгой тактической игры"
          },
          body: {
            en: "Skill-based tactical card duels, animated cards, character-driven opponents, campaign progression, online friend play, and a cheating boss hook give the game a clear long-session structure.",
            ru: "Тактические карточные дуэли, анимированные карты, соперники с характером, прогрессия кампании, онлайн с другом и hook с нечестными боссами дают игре понятную структуру долгих сессий."
          },
          images: witchBroomSections["boss-campaign"]
        },
        {
          id: "tactical-magic",
          eyebrow: {
            en: "Tactical magic",
            ru: "Тактическая магия"
          },
          title: {
            en: "Magic creates spikes without replacing skill",
            ru: "Магия создает всплески, но не заменяет мастерство"
          },
          body: {
            en: "Magic acts as a controlled modifier: it raises PvE drama and creates sharp tactical moments while balanced modes keep the focus on decisions.",
            ru: "Магия работает как контролируемый модификатор: усиливает PvE-драму и создает острые тактические моменты, пока сбалансированные режимы держат фокус на решениях."
          },
          images: witchBroomSections["tactical-magic"]
        },
        {
          id: "online-friend-duels",
          eyebrow: {
            en: "Campaign & opponents",
            ru: "Кампания и соперники"
          },
          title: {
            en: "Bosses are collectible rivals with personality",
            ru: "Боссы - collectible rivals с характером"
          },
          body: {
            en: "Each opponent has a target score, personality, animated identity, and unique cheating pattern. A strong win unlocks the next rival; catching a cheating boss unlocks magical items and new modes.",
            ru: "У каждого соперника есть цель по очкам, характер, анимированная идентичность и уникальный cheating pattern. Сильная победа открывает следующего rival, а поимка читера открывает магические предметы и новые режимы."
          },
          images: witchBroomSections["online-friend-duels"]
        }
      ]
    }
  },
  {
    id: "bridges",
    slug: "bridges",
    title: {
      en: "Bridges",
      ru: "Bridges"
    },
    tagline: {
      en: "Connect the islands. Relax and sharpen your mind.",
      ru: "Соединяйте острова. Отдыхайте и тренируйте логику."
    },
    description: {
      en: "Build bridges between tropical islands to solve satisfying logic puzzles. Explore the Arcade map or play Classic grids, choose your difficulty, and enjoy a peaceful island escape with calming music and helpful hints.",
      ru: "Стройте мосты между тропическими островами и решайте логические головоломки. Исследуйте карту в режиме Arcade или выбирайте поля Classic, настройте сложность и отдыхайте под спокойную музыку с подсказками на случай трудных задач."
    },
    status: "live",
    genre: {
      en: "Island logic puzzles",
      ru: "Логические головоломки"
    },
    logo: bridgesAssets.icon,
    heroImage: bridgesAssets.hero,
    heroScreenshots: [bridgesGallery.home, bridgesGallery.gameplay],
    gradient: "linear-gradient(135deg, #164d81 0%, #22b8ce 48%, #78d8bc 100%)",
    accentColor: "#78d8bc",
    links: bridgesGame.links,
    gallery: [
      {
        src: bridgesGallery.home,
        alt: { en: "Bridges main menu", ru: "Главное меню Bridges" },
        tone: "#22b8ce"
      },
      {
        src: bridgesGallery.arcade,
        alt: { en: "Bridges Arcade map", ru: "Карта Arcade в Bridges" },
        tone: "#78d8bc"
      },
      {
        src: bridgesGallery.gameplay,
        alt: { en: "Bridges island gameplay", ru: "Острова и мосты в Bridges" },
        tone: "#ffd84d"
      },
      {
        src: bridgesGallery.puzzle,
        alt: { en: "Bridges logic puzzle", ru: "Логическая головоломка Bridges" },
        tone: "#22b8ce"
      }
    ],
    highlights: [
      { en: "Relaxing tropical islands and calming music", ru: "Тропические острова и спокойная музыка" },
      { en: "Arcade adventures and Classic puzzles", ru: "Приключения Arcade и головоломки Classic" },
      { en: "Easy, Medium, and Hard difficulty", ru: "Лёгкая, средняя и высокая сложность" },
      { en: "Haptic feedback and helpful hints", ru: "Тактильный отклик и полезные подсказки" }
    ],
    site: {
      storeMaterialsSource: "https://nvp-rnd-lab.web.app/projects/bridges.html",
      howEyebrow: { en: "How to play", ru: "Как играть" },
      howTitle: { en: "A few bridges. A satisfying solution.", ru: "Несколько мостов — и решение найдено." },
      howToPlay: [
        {
          title: { en: "Choose your journey", ru: "Выберите приключение" },
          body: {
            en: "Explore the Arcade map or start a Classic puzzle at your preferred difficulty.",
            ru: "Отправляйтесь по карте Arcade или начните головоломку Classic подходящей сложности."
          },
          primitive: "node"
        },
        {
          title: { en: "Read the islands", ru: "Изучите острова" },
          body: {
            en: "Each island's number tells you how many bridges it needs.",
            ru: "Число на каждом острове подсказывает, сколько мостов ему нужно."
          },
          primitive: "node"
        },
        {
          title: { en: "Build the bridges", ru: "Постройте мосты" },
          body: {
            en: "Connect the islands with the correct number of bridges to solve the grid.",
            ru: "Соедините острова нужным количеством мостов, чтобы решить головоломку."
          },
          primitive: "edge"
        },
        {
          title: { en: "Find your next move", ru: "Найдите следующий ход" },
          body: {
            en: "Take your time, enjoy the island atmosphere, and use a hint when you get stuck.",
            ru: "Играйте в своём темпе, наслаждайтесь атмосферой островов и используйте подсказку, если застряли."
          },
          primitive: "glow"
        }
      ],
      modesEyebrow: { en: "Ways to play", ru: "Режимы игры" },
      modesTitle: { en: "Your island getaway, your pace", ru: "Ваши острова, ваш темп" },
      modes: [
        {
          id: "arcade",
          title: { en: "Arcade adventure", ru: "Приключение Arcade" },
          body: {
            en: "Journey through the island map and solve puzzles along the way.",
            ru: "Путешествуйте по карте островов и решайте головоломки по пути."
          },
          tag: { en: "Arcade", ru: "Arcade" }
        },
        {
          id: "classic",
          title: { en: "Classic puzzles", ru: "Головоломки Classic" },
          body: {
            en: "Test your logic with a fresh grid whenever you want a puzzle break.",
            ru: "Проверяйте логику на новом поле, когда захочется перерыва с головоломкой."
          },
          tag: { en: "Classic", ru: "Classic" }
        },
        {
          id: "difficulty",
          title: { en: "Choose the challenge", ru: "Выберите сложность" },
          body: {
            en: "Choose Easy, Medium, or Hard grids to match your mood and puzzle-solving skills.",
            ru: "Выбирайте лёгкие, средние или сложные поля под настроение и свой опыт."
          },
          tag: { en: "3 difficulties", ru: "3 уровня сложности" }
        }
      ],
      sections: [
        {
          id: "island-adventure",
          eyebrow: { en: "A tropical escape", ru: "Отдых на островах" },
          title: { en: "A peaceful place for a clever challenge", ru: "Спокойное место для интересных задач" },
          body: {
            en: "Colorful islands, calming music, and satisfying visual effects turn each puzzle into a small getaway. Start from the main menu and follow your journey across the Arcade map.",
            ru: "Красочные острова, спокойная музыка и приятные визуальные эффекты превращают каждую головоломку в маленький отпуск. Начните с главного меню и отправляйтесь в путешествие по карте Arcade."
          },
          images: [bridgesGallery.home, bridgesGallery.arcade]
        },
        {
          id: "build-and-solve",
          eyebrow: { en: "Relax and think", ru: "Отдыхайте и размышляйте" },
          title: { en: "Every bridge brings you closer", ru: "Каждый мост приближает к решению" },
          body: {
            en: "Build the connections each island needs and feel every bridge with haptic feedback. When a puzzle gets tricky, helpful hints guide you toward the solution.",
            ru: "Стройте нужные островам соединения и ощущайте каждый мост благодаря тактильному отклику. Если задача окажется трудной, подсказки помогут найти путь к решению."
          },
          images: [bridgesGallery.gameplay, bridgesGallery.puzzle]
        }
      ]
    }
  }
] as const satisfies readonly GameMetadata[];

export const siteFeaturedGame = siteGames[0];
const witchBroomSiteGame = siteGames[1];
const bridgesSiteGame = siteGames[2];

export const homeShowcaseGames = [
  {
    id: siteFeaturedGame.id,
    slug: siteFeaturedGame.slug,
    title: siteFeaturedGame.title,
    tagline: siteFeaturedGame.tagline,
    description: siteFeaturedGame.description,
    status: "live",
    genre: siteFeaturedGame.genre,
    image: diceTrianglesAssets.card ?? siteFeaturedGame.heroImage,
    gradient: siteFeaturedGame.gradient,
    accentColor: siteFeaturedGame.accentColor,
    visual: "dice-triangles"
  },
  {
    id: witchBroomSiteGame.id,
    slug: witchBroomSiteGame.slug,
    title: witchBroomSiteGame.title,
    tagline: witchBroomSiteGame.tagline,
    description: witchBroomSiteGame.description,
    status: "live",
    genre: witchBroomSiteGame.genre,
    image: witchBroomAssets.card ?? witchBroomSiteGame.heroImage,
    gradient: witchBroomSiteGame.gradient,
    accentColor: witchBroomSiteGame.accentColor,
    visual: "witch-broom"
  },
  {
    id: bridgesSiteGame.id,
    slug: bridgesSiteGame.slug,
    title: bridgesSiteGame.title,
    tagline: bridgesSiteGame.tagline,
    description: bridgesSiteGame.description,
    status: "live",
    genre: bridgesSiteGame.genre,
    image: bridgesAssets.card,
    imageFit: "contain",
    gradient: bridgesSiteGame.gradient,
    accentColor: bridgesSiteGame.accentColor,
    visual: "bridges"
  }
] as const satisfies readonly GameShowcaseMetadata[];

export const secondaryGames = siteGames.filter(
  (game) => game.id !== siteFeaturedGame.id
);

export function findGameBySlug(slug: string) {
  return siteGames.find((game) => game.slug === slug);
}
